import React, { useState } from 'react';
import { Dropdown, Badge, Form, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faGlobe, faCheck, faUser } from '@fortawesome/free-solid-svg-icons';
import { useTimezone } from '../../contexts/TimezoneContext';
import './TimezoneSelector.css';

/**
 * TimezoneSelector Component
 *
 * Allows users to select and change the global timezone for the application.
 * All dates are stored in UTC and displayed in the selected timezone.
 */

// Common timezones grouped by region
const TIMEZONES = {
    'Americas': [
        { value: 'America/New_York', label: 'New York (EST/EDT)' },
        { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
        { value: 'America/Denver', label: 'Denver (MST/MDT)' },
        { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
        { value: 'America/Phoenix', label: 'Phoenix (MST)' },
        { value: 'America/Toronto', label: 'Toronto' },
        { value: 'America/Vancouver', label: 'Vancouver' },
        { value: 'America/Mexico_City', label: 'Mexico City' },
        { value: 'America/Sao_Paulo', label: 'São Paulo' },
        { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
    ],
    'Europe': [
        { value: 'Europe/London', label: 'London (GMT/BST)' },
        { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
        { value: 'Europe/Berlin', label: 'Berlin' },
        { value: 'Europe/Madrid', label: 'Madrid' },
        { value: 'Europe/Rome', label: 'Rome' },
        { value: 'Europe/Amsterdam', label: 'Amsterdam' },
        { value: 'Europe/Brussels', label: 'Brussels' },
        { value: 'Europe/Zurich', label: 'Zurich' },
        { value: 'Europe/Stockholm', label: 'Stockholm' },
        { value: 'Europe/Moscow', label: 'Moscow' },
    ],
    'Asia': [
        { value: 'Asia/Dubai', label: 'Dubai' },
        { value: 'Asia/Kolkata', label: 'India (IST)' },
        { value: 'Asia/Bangkok', label: 'Bangkok' },
        { value: 'Asia/Singapore', label: 'Singapore' },
        { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
        { value: 'Asia/Shanghai', label: 'Shanghai' },
        { value: 'Asia/Tokyo', label: 'Tokyo' },
        { value: 'Asia/Seoul', label: 'Seoul' },
    ],
    'Pacific': [
        { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
        { value: 'Australia/Melbourne', label: 'Melbourne' },
        { value: 'Australia/Perth', label: 'Perth' },
        { value: 'Pacific/Auckland', label: 'Auckland' },
        { value: 'Pacific/Fiji', label: 'Fiji' },
        { value: 'Pacific/Honolulu', label: 'Honolulu' },
    ],
    'Other': [
        { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    ]
};

function TimezoneSelector({ variant = 'dropdown', bondTimezone = null, bondTimezones = [] }) {
    const {
        timezone,
        timezoneLabel,
        changeTimezone,
        resetToBrowserTimezone,
        getTimezoneOffset,
        browserTimezone
    } = useTimezone();

    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Get user's detected timezone from browser
    const userLocalTimezone = browserTimezone;

    const handleTimezoneChange = (tz) => {
        changeTimezone(tz);
        setShowModal(false);
    };

    // Filter timezones based on search
    const getFilteredTimezones = () => {
        if (!searchTerm) return TIMEZONES;

        const filtered = {};
        Object.keys(TIMEZONES).forEach(region => {
            const matches = TIMEZONES[region].filter(tz =>
                tz.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tz.value.toLowerCase().includes(searchTerm.toLowerCase())
            );
            if (matches.length > 0) {
                filtered[region] = matches;
            }
        });
        return filtered;
    };

    const filteredTimezones = getFilteredTimezones();

    if (variant === 'badge') {
        // Determine the display text for the badge
        const getCityName = (tz) => {
            return tz.split('/').pop().replace(/_/g, ' ');
        };

        const getBadgeText = () => {
            const offset = getTimezoneOffset();
            const city = getCityName(timezone);

            if (timezone === 'UTC') {
                return 'UTC';
            } else if (timezone === userLocalTimezone) {
                return `Local (${city}) ${offset}`;
            } else if (bondTimezones && bondTimezones.includes(timezone)) {
                return `User (${city}) ${offset}`;
            } else {
                return `${city} ${offset}`;
            }
        };

        // Determine badge color based on timezone type
        const isUserTimezone = bondTimezones && bondTimezones.includes(timezone);
        const badgeVariant = isUserTimezone ? 'info' : 'secondary';

        return (
            <>
                <Badge
                    bg={badgeVariant}
                    className="timezone-badge"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowModal(true)}
                    title="Click to change timezone"
                >
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {getBadgeText()}
                </Badge>

                <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FontAwesomeIcon icon={faGlobe} className="me-2" />
                            Select Timezone
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Search timezones..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <h6 className="text-muted mb-2">
                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                Quick Select:
                            </h6>
                            <div className="timezone-quick-select">
                                <Button
                                    variant={timezone === 'UTC' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => handleTimezoneChange('UTC')}
                                >
                                    <FontAwesomeIcon icon={faGlobe} className="me-1" />
                                    UTC
                                    {timezone === 'UTC' && <FontAwesomeIcon icon={faCheck} className="ms-1" />}
                                </Button>

                                <Button
                                    variant={timezone === userLocalTimezone ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => handleTimezoneChange(userLocalTimezone)}
                                >
                                    <FontAwesomeIcon icon={faGlobe} className="me-1" />
                                    Local ({userLocalTimezone.split('/').pop()})
                                    {timezone === userLocalTimezone && <FontAwesomeIcon icon={faCheck} className="ms-1" />}
                                </Button>

                                {bondTimezones && bondTimezones.length > 0 && bondTimezones.map(bondTz => (
                                    <Button
                                        key={`bond-${bondTz}`}
                                        variant={timezone === bondTz ? 'info' : 'outline-info'}
                                        size="sm"
                                        onClick={() => handleTimezoneChange(bondTz)}
                                    >
                                        <FontAwesomeIcon icon={faUser} className="me-1" />
                                        User ({bondTz.split('/').pop()})
                                        {timezone === bondTz && <FontAwesomeIcon icon={faCheck} className="ms-1" />}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <hr />

                        <div className="timezone-list">
                            {Object.keys(filteredTimezones).map(region => (
                                <div key={region} className="mb-3">
                                    <h6 className="text-muted">{region}</h6>
                                    {filteredTimezones[region].map(tz => (
                                        <div
                                            key={tz.value}
                                            className={`timezone-option ${timezone === tz.value ? 'selected' : ''}`}
                                            onClick={() => handleTimezoneChange(tz.value)}
                                        >
                                            <div>
                                                {tz.label}
                                                {timezone === tz.value && (
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        className="ms-2 text-success"
                                                    />
                                                )}
                                            </div>
                                            <small className="text-muted">{tz.value}</small>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                </Modal>
            </>
        );
    }

    // Dropdown variant
    return (
        <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm" id="timezone-dropdown">
                <FontAwesomeIcon icon={faClock} className="me-2" />
                {timezoneLabel} ({getTimezoneOffset()})
            </Dropdown.Toggle>

            <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Dropdown.Header>
                    <FontAwesomeIcon icon={faGlobe} className="me-2" />
                    Select Timezone
                </Dropdown.Header>

                <Dropdown.Item onClick={resetToBrowserTimezone}>
                    <strong>Browser Default</strong>
                    <br />
                    <small className="text-muted">{browserTimezone}</small>
                </Dropdown.Item>

                <Dropdown.Divider />

                {Object.keys(TIMEZONES).map(region => (
                    <React.Fragment key={region}>
                        <Dropdown.Header>{region}</Dropdown.Header>
                        {TIMEZONES[region].map(tz => (
                            <Dropdown.Item
                                key={tz.value}
                                onClick={() => changeTimezone(tz.value)}
                                active={timezone === tz.value}
                            >
                                {tz.label}
                                {timezone === tz.value && (
                                    <FontAwesomeIcon icon={faCheck} className="ms-2" />
                                )}
                            </Dropdown.Item>
                        ))}
                    </React.Fragment>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default TimezoneSelector;
